import React, { useState, useContext } from 'react';
import { Edit, History, Clock, Check, ChevronDown, Link as LinkIcon, ShoppingCart, Share2, Eye, Tag, Calendar, AlertCircle } from 'lucide-react';
import AppContext from '../../context/AppContext';
import { useParams, useNavigate } from 'react-router-dom';

export default function ProductCard() {
  const [showHistory, setShowHistory] = useState(false);
  const [copyStatus, setCopyStatus] = useState({ show: false, success: false, message: '' });
  const { userProducts, id } = useContext(AppContext);
  const navigate = useNavigate();
  const { idProduit } = useParams();

  // Récupérer le produit à partir des produits de l'utilisateur
  const getProductById = (productId) => {
    console.log("DEBUG - ProductCard - Recherche du produit:", productId);
    console.log("DEBUG - ProductCard - userProducts:", userProducts);
    
    // Si userProducts est un tableau (ancienne méthode)
    if (Array.isArray(userProducts)) {
      console.log("DEBUG - ProductCard - userProducts est un tableau");
      return userProducts.find(product => product.id === productId.toString());
    }
    
    // Si userProducts est un objet avec des IDs comme clés (nouvelle structure)
    if (userProducts && typeof userProducts === 'object') {
      console.log("DEBUG - ProductCard - userProducts est un objet");
      
      // Vérifier si le produit est directement accessible par son ID
      if (userProducts[productId]) {
        console.log("DEBUG - ProductCard - Produit trouvé directement par ID");
        return userProducts[productId];
      }
      
      // Sinon, parcourir tous les produits (structure imbriquée)
      for (const userId in userProducts) {
        if (userProducts[userId] && typeof userProducts[userId] === 'object') {
          // Si c'est la structure par utilisateur
          if (userProducts[userId][productId]) {
            console.log("DEBUG - ProductCard - Produit trouvé dans sous-objet utilisateur");
            return userProducts[userId][productId];
          }
          
          // Vérifier si c'est un objet de produits
          for (const prodId in userProducts[userId]) {
            const product = userProducts[userId][prodId];
            if (product && product.id === productId.toString()) {
              console.log("DEBUG - ProductCard - Produit trouvé par correspondance d'ID");
              return product;
            }
          }
        }
      }
    }
    
    console.log("DEBUG - ProductCard - Produit non trouvé");
    return null;
  };
  
  const productId = idProduit;
  const foundProduct = getProductById(productId);

  // Utiliser un produit par défaut si non trouvé
  const data = foundProduct || {
    id: productId || "unknown",
    title: "Produit introuvable",
    price: 0,
    description: "Les détails de ce produit ne sont pas disponibles pour le moment.",
    duration: "N/A",
    imageUrl: "", 
    status: "inactive",
    created_at: new Date().toISOString()
  };

  // Données de test pour l'historique
  const purchaseHistory = [
    { date: '2024-01-15', amount: 299.99, client: "Sophie Martin" },
    { date: '2024-01-10', amount: 299.99, client: "Jean Dupont" },
    { date: '2024-01-05', amount: 299.99, client: "Marie Bernard" },
  ];
  
  const editHistory = [
    { date: '2024-01-12', field: 'prix', oldValue: '249.99', newValue: '299.99' },
    { date: '2024-01-05', field: 'description', type: 'Mise à jour contenu' },
  ];

  // Fonction de copie sécurisée avec multiples méthodes de fallback
  const copyProductLink = () => {
    const productLink = `https://t.me/KappelaclientBot/achat?startapp=${id}-${data.id}`;
    
    // Méthode 1: Essayer d'utiliser l'API Clipboard moderne
    const copyWithClipboardAPI = () => {
      return navigator.clipboard.writeText(productLink);
    };
    
    // Méthode 2: Utiliser la méthode de l'élément input temporaire (fonctionne sur plus de navigateurs)
    const copyWithDOMElement = () => {
      return new Promise((resolve, reject) => {
        try {
          // Créer un élément input temporaire
          const tempInput = document.createElement('input');
          tempInput.value = productLink;
          tempInput.setAttribute('readonly', ''); // Évite le focus sur mobile
          tempInput.style.position = 'absolute';
          tempInput.style.left = '-9999px'; // Hors écran
          document.body.appendChild(tempInput);
          
          // Sélectionner et copier
          tempInput.select();
          tempInput.setSelectionRange(0, 99999); // Pour mobile
          const successful = document.execCommand('copy');
          
          // Nettoyer
          document.body.removeChild(tempInput);
          
          if (successful) {
            resolve();
          } else {
            reject(new Error('execCommand copy failed'));
          }
        } catch (err) {
          reject(err);
        }
      });
    };
    
    // Essayer les différentes méthodes en séquence
    copyWithClipboardAPI()
      .then(() => {
        // Succès avec l'API Clipboard
        setCopyStatus({ show: true, success: true, message: 'Lien copié avec succès!' });
      })
      .catch(() => {
        // Si l'API Clipboard échoue, essayer avec l'élément DOM
        copyWithDOMElement()
          .then(() => {
            setCopyStatus({ show: true, success: true, message: 'Lien copié avec succès!' });
          })
          .catch(err => {
            console.error('Toutes les méthodes de copie ont échoué:', err);
            setCopyStatus({ 
              show: true, 
              success: false, 
              message: 'Impossible de copier automatiquement. Cliquez sur le lien pour le sélectionner.' 
            });
          });
      });
      
    // Masquer le message après 3 secondes
    setTimeout(() => setCopyStatus({ show: false, success: false, message: '' }), 3000);
  };

  // Formater la date
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  // Déterminer le statut du produit
  const getStatusBadge = () => {
    switch(data.status) {
      case 'active':
        return <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">Actif</span>;
      case 'inactive':
        return <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">Inactif</span>;
      case 'pending':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">En attente</span>;
      default:
        return <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">Produit</span>;
    }
  };

  return (
    <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg overflow-hidden mb-8">
      {/* Header avec image */}
      <div className="relative">
        <div className="aspect-video w-full bg-gradient-to-r from-blue-500 to-indigo-600 overflow-hidden">
          {data?.imageUrl ? (
            <img
              src={data.imageUrl}
              alt={data.title || "Image du produit"}
              className="object-cover w-full h-full"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/800x400?text=Image+non+disponible";
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingCart className="w-24 h-24 text-white opacity-60" />
            </div>
          )}
          
          {/* Overlay contenant le titre et le prix */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-white">
            <div className="flex justify-between items-end">
              <div>
                <h1 className="text-2xl font-bold mb-2 drop-shadow-md">{data.title}</h1>
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4" />
                  <span>Créé le {formatDate(data.created_at)}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="text-3xl font-bold drop-shadow-md">{Number(data?.price || 0).toLocaleString('fr-FR')} FCFA</span>
                {getStatusBadge()}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Notification de copie */}
      {copyStatus.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg flex items-center gap-2 shadow-lg animate-fade-in max-w-md ${
          copyStatus.success ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
        }`}>
          {copyStatus.success ? (
            <Check className="h-5 w-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="h-5 w-5 flex-shrink-0" />
          )}
          <span>{copyStatus.message}</span>
        </div>
      )}
      
      {/* Contenu principal */}
      <div className="grid md:grid-cols-3 gap-6 p-6">
        {/* Colonne 1: Statistiques */}
        <div className="space-y-4">
          <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <Eye className="w-4 h-4 text-blue-600" />
            Aperçu rapide
          </h3>
          
          <div className="grid grid-cols-1 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 hover:bg-blue-100 transition-colors">
              <div className="flex justify-between items-start mb-1">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
                <span className="text-xs text-blue-700 font-medium bg-blue-200 px-2 py-0.5 rounded-full">Total</span>
              </div>
              <p className="text-2xl font-bold text-blue-700 mt-1">{purchaseHistory.length}</p>
              <p className="text-sm text-blue-600">Ventes</p>
            </div>
            
            <div className="bg-purple-50 rounded-xl p-4 hover:bg-purple-100 transition-colors">
              <div className="flex justify-between items-start mb-1">
                <Clock className="w-6 h-6 text-purple-600" />
                <span className="text-xs text-purple-700 font-medium bg-purple-200 px-2 py-0.5 rounded-full">Durée</span>
              </div>
              <p className="text-2xl font-bold text-purple-700 mt-1">{data.duration}</p>
              <p className="text-sm text-purple-600">Abonnement</p>
            </div>
            
            <div className="bg-green-50 rounded-xl p-4 hover:bg-green-100 transition-colors">
              <div className="flex justify-between items-start mb-1">
                <Tag className="w-6 h-6 text-green-600" />
                <span className="text-xs text-green-700 font-medium bg-green-200 px-2 py-0.5 rounded-full">Revenu</span>
              </div>
              <p className="text-2xl font-bold text-green-700 mt-1">
                {(purchaseHistory.reduce((sum, p) => sum + p.amount, 0)).toLocaleString('fr-FR')} FCFA
              </p>
              <p className="text-sm text-green-600">Total des ventes</p>
            </div>
          </div>
          
          {/* Bloc de partage */}
          <div className="mt-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h3 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <Share2 className="w-4 h-4 text-gray-600" />
              Partager le produit
            </h3>
            
            <div className="relative mt-2">
              {!copyStatus.success && copyStatus.show && !copyStatus.success && (
                <div className="truncate p-2.5 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 text-sm mb-2 cursor-pointer hover:bg-gray-200 transition-colors" 
                     onClick={() => copyProductLink()}>
                  https://t.me/KappelaclientBot/achat?startapp={id}-{data.id}
                </div>
              )}
              
              <button
                onClick={copyProductLink}
                className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <LinkIcon className="w-4 h-4" />
                Copier le lien
              </button>
            </div>
          </div>
        </div>
        
        {/* Colonne 2-3: Description et actions */}
        <div className="md:col-span-2 space-y-6">
          <div className="bg-blue-50 rounded-xl p-5 border-l-4 border-blue-500">
            <h3 className="font-semibold text-gray-800 mb-2">Description</h3>
            <p className="text-gray-700 leading-relaxed">
              {data.description || "Aucune description disponible pour ce produit."}
            </p>
          </div>
          
          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={() => navigate(`/ModifierProduit/${idProduit}`)}
              className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl flex items-center justify-center gap-2 transition-colors shadow-md"
            >
              <Edit className="w-5 h-5" />
              Modifier le produit
            </button>
          </div>

          {/* Expandable History */}
        
        {

          /*
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full px-4 py-4 bg-gray-50 hover:bg-gray-100 text-left flex items-center justify-between text-gray-800 font-medium transition-colors"
            >
              <span className="flex items-center gap-2">
                <History className="w-5 h-5 text-gray-600" />
                Historique du produit
              </span>
              <ChevronDown className={`w-5 h-5 transition-transform duration-200 ${showHistory ? 'rotate-180' : ''}`} />
            </button>
            
            {showHistory && (
              <div className="p-5 space-y-5 bg-white">
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-800 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                    Dernières ventes
                  </h3>
                  
                  {purchaseHistory.length > 0 ? (
                    <div className="space-y-2">
                      {purchaseHistory.map((purchase, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div>
                            <span className="font-medium text-gray-800">{purchase.client}</span>
                            <span className="text-gray-500 ml-2 text-sm">
                              {new Date(purchase.date).toLocaleDateString('fr-FR')}
                            </span>
                          </div>
                          <span className="text-green-600 font-medium">{purchase.amount.toLocaleString('fr-FR')} FCFA</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic text-sm">Aucune vente enregistrée pour ce produit.</p>
                  )}
                </div>

                <div className="space-y-3 border-t border-gray-100 pt-4">
                  <h3 className="font-medium text-gray-800 flex items-center gap-2">
                    <Edit className="w-5 h-5 text-indigo-600" />
                    Modifications
                  </h3>
                  
                  {editHistory.length > 0 ? (
                    <div className="space-y-2">
                      {editHistory.map((edit, idx) => (
                        <div key={idx} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-700 font-medium">{edit.field}</span>
                            <span className="text-gray-500 text-sm">{new Date(edit.date).toLocaleDateString('fr-FR')}</span>
                          </div>
                          {edit.oldValue && (
                            <div className="mt-1 text-sm">
                              <span className="text-red-500 line-through mr-2">{edit.oldValue}</span>
                              <span className="text-green-600">{edit.newValue}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic text-sm">Aucune modification enregistrée pour ce produit.</p>
                  )}
                </div>
              </div>
            )}
          </div>
          */
        }
        </div>
      </div>
    </div>
  );
}